import { useEffect } from 'react'
import { useTranslation } from '../../i18n'
import { Button } from '../ui'
import { useTheme } from '../../hooks/useTheme'

interface PauseSetModalProps {
  isOpen: boolean
  isPausing: boolean
  pauseStartDate: string
  pauseEndDate: string
  onPauseStartDateChange: (value: string) => void
  onPauseEndDateChange: (value: string) => void
  onConfirm: () => void
  onCancel: () => void
}

export function PauseSetModal({
  isOpen,
  isPausing,
  pauseStartDate,
  pauseEndDate,
  onPauseStartDateChange,
  onPauseEndDateChange,
  onConfirm,
  onCancel
}: PauseSetModalProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  
  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0]
  
  // Initialize dates if empty
  useEffect(() => {
    if (isOpen && !pauseStartDate) {
      // Default to tomorrow
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      onPauseStartDateChange(tomorrow.toISOString().split('T')[0])
    }
    if (isOpen && !pauseEndDate) {
      // Default to 7 days from tomorrow
      const weekLater = new Date()
      weekLater.setDate(weekLater.getDate() + 8)
      onPauseEndDateChange(weekLater.toISOString().split('T')[0])
    }
  }, [isOpen, pauseStartDate, pauseEndDate, onPauseStartDateChange, onPauseEndDateChange])
  
  if (!isOpen) return null

  const isEndDateBeforeStart = pauseEndDate && pauseStartDate && pauseEndDate < pauseStartDate
  const isStartDateInPast = pauseStartDate && pauseStartDate < today
  const isEndDateInPast = pauseEndDate && pauseEndDate < today
  const isValid = pauseStartDate && pauseEndDate && !isEndDateBeforeStart && !isStartDateInPast && !isEndDateInPast

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div 
        className="rounded-2xl p-6 max-w-md w-full"
        style={{
          backgroundColor: theme.bgColor,
          color: theme.textColor,
        }}
      >
        <h3 className="text-xl font-bold mb-4">{t.modal.pause.title}</h3>

        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-semibold mb-2">
              {t.modal.pause.startDate}
            </label>
            <input
              type="date"
              value={pauseStartDate}
              onChange={(e) => onPauseStartDateChange(e.target.value)}
              min={today}
              disabled={isPausing}
              className="w-full rounded-lg p-3 border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.cardBg,
                borderColor: isStartDateInPast ? 'rgba(239, 68, 68, 0.5)' : theme.cardBorder,
                color: theme.textColor,
              }}
            />
            {isStartDateInPast && (
              <p className="text-xs text-red-400 mt-1">{t.modal.pause.errors.startDateInPast}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">
              {t.modal.pause.endDate}
            </label>
            <input
              type="date"
              value={pauseEndDate}
              onChange={(e) => onPauseEndDateChange(e.target.value)}
              min={pauseStartDate || today}
              disabled={isPausing}
              className="w-full rounded-lg p-3 border transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: theme.cardBg,
                borderColor: isEndDateBeforeStart || isEndDateInPast ? 'rgba(239, 68, 68, 0.5)' : theme.cardBorder,
                color: theme.textColor,
              }}
            />
            {isEndDateBeforeStart && (
              <p className="text-xs text-red-400 mt-1">{t.modal.pause.errors.endDateBeforeStart}</p>
            )}
            {isEndDateInPast && !isEndDateBeforeStart && (
              <p className="text-xs text-red-400 mt-1">{t.modal.pause.errors.endDateInPast}</p>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="secondary"
            size="md"
            onClick={onCancel}
            disabled={isPausing}
            fullWidth
          >
            {t.modal.pause.cancel}
          </Button>
          <Button
            variant={isValid && !isPausing ? 'warning' : 'secondary'}
            size="md"
            onClick={onConfirm}
            disabled={isPausing || !isValid}
            fullWidth
          >
            {isPausing ? t.trainingSetActions.pausing : t.modal.pause.confirm}
          </Button>
        </div>
      </div>
    </div>
  )
}
